import { useCallback } from 'react';
import { useCreateMilestoneMutation, useUpdateMilestoneMutation, useDeleteMilestoneMutation } from '../../__generated__/chirp/com/hubspot/deal/plans/rpc/DealPlanPortalRpc.hooks';
import { GET_DEAL_PLAN_QUERY_FIELD_NAME } from '../../__generated__/chirp/com/hubspot/deal/plans/rpc/DealPlanPortalRpc.dfc.new';
import { assigneeToOwner } from 'deal-plans-lib/utils/ownerUtils';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import I18n from 'I18n';

// maps milestoneId to React key so milestones rows don't re-render when tempId is replaced by the server-assigned ID
const milestoneClientKeys = new Map();
export function getMilestoneClientKey(milestoneId) {
  var _milestoneClientKeys$;
  return (_milestoneClientKeys$ = milestoneClientKeys.get(milestoneId)) !== null && _milestoneClientKeys$ !== void 0 ? _milestoneClientKeys$ : String(milestoneId);
}
export function useCreateMilestone(dealPlanId) {
  const [createMilestoneMutation] = useCreateMilestoneMutation();
  const createMilestone = useCallback(title => {
    const tempId = -Date.now();
    const clientKey = String(-tempId);
    milestoneClientKeys.set(tempId, clientKey);
    void createMilestoneMutation({
      variables: {
        dealPlanId,
        subject: title
      },
      optimisticResponse: {
        result: {
          milestoneId: tempId
        }
      },
      update: (cache, {
        data
      }) => {
        var _data$result$mileston, _data$result;
        const newMilestoneId = (_data$result$mileston = data === null || data === void 0 || (_data$result = data.result) === null || _data$result === void 0 ? void 0 : _data$result.milestoneId) !== null && _data$result$mileston !== void 0 ? _data$result$mileston : tempId;
        if (newMilestoneId !== tempId) {
          milestoneClientKeys.set(newMilestoneId, clientKey);
          milestoneClientKeys.delete(tempId);
        }
        cache.modify({
          fields: {
            [GET_DEAL_PLAN_QUERY_FIELD_NAME]: existing => Object.assign({}, existing, {
              milestones: [...existing.milestones.filter(m => m.milestoneId !== tempId), {
                milestoneId: newMilestoneId,
                title,
                status: 'not_started'
              }]
            })
          }
        });
      },
      onCompleted: () => {
        FloatingAlertStore.addAlert({
          type: 'success',
          message: I18n.text('dealPlan.milestones.updateSuccess')
        });
      },
      onError: error => {
        console.error(error);
        FloatingAlertStore.addAlert({
          type: 'danger',
          message: I18n.text('dealPlan.milestones.createError')
        });
      }
    });
  }, [createMilestoneMutation, dealPlanId]);
  return {
    createMilestone
  };
}
export function useUpdateMilestone() {
  const [updateMilestoneMutation] = useUpdateMilestoneMutation();
  const updateMilestone = useCallback((milestone, updates) => {
    var _ref, _updates$subject, _ref2, _updates$status, _updates$dueDate;
    void updateMilestoneMutation({
      variables: {
        milestoneId: milestone.milestoneId,
        subject: updates.subject,
        status: updates.status,
        dueDate: updates.dueDate,
        owner: updates.owner != null ? assigneeToOwner(updates.owner) : undefined
      },
      optimisticResponse: {
        result: {
          milestoneId: milestone.milestoneId,
          subject: (_ref = (_updates$subject = updates.subject) !== null && _updates$subject !== void 0 ? _updates$subject : milestone.title) !== null && _ref !== void 0 ? _ref : '',
          status: (_ref2 = (_updates$status = updates.status) !== null && _updates$status !== void 0 ? _updates$status : milestone.status) !== null && _ref2 !== void 0 ? _ref2 : 'not_started',
          dueDate: updates.dueDate !== undefined ? (_updates$dueDate = updates.dueDate) !== null && _updates$dueDate !== void 0 ? _updates$dueDate : undefined : milestone.dueDate
        }
      },
      update: cache => {
        cache.modify({
          fields: {
            [GET_DEAL_PLAN_QUERY_FIELD_NAME]: existing => Object.assign({}, existing, {
              milestones: existing.milestones.map(m => {
                if (m.milestoneId !== milestone.milestoneId) return m;
                return Object.assign({}, m, {
                  milestoneId: m.milestoneId,
                  title: updates.subject !== undefined ? updates.subject : m.title,
                  status: updates.status !== undefined ? updates.status : m.status,
                  dueDate: updates.dueDate !== undefined ? updates.dueDate : m.dueDate,
                  owner: updates.owner !== undefined ? updates.owner : m.owner
                });
              })
            })
          }
        });
      },
      onCompleted: () => {
        FloatingAlertStore.addAlert({
          type: 'success',
          message: I18n.text('dealPlan.milestones.updateSuccess')
        });
      },
      onError: error => {
        console.error(error);
        FloatingAlertStore.addAlert({
          type: 'danger',
          message: I18n.text('dealPlan.milestones.updateError')
        });
      }
    });
  }, [updateMilestoneMutation]);
  return {
    updateMilestone
  };
}
export function useDeleteMilestone() {
  const [deleteMilestoneMutation] = useDeleteMilestoneMutation();
  const deleteMilestone = useCallback(milestoneId => {
    void deleteMilestoneMutation({
      variables: {
        milestoneId
      },
      optimisticResponse: {
        result: {
          milestoneId
        }
      },
      update: cache => {
        milestoneClientKeys.delete(milestoneId);
        cache.modify({
          fields: {
            [GET_DEAL_PLAN_QUERY_FIELD_NAME]: existing => Object.assign({}, existing, {
              milestones: existing.milestones.filter(m => m.milestoneId !== milestoneId)
            })
          }
        });
      },
      onError: error => {
        console.error(error);
        FloatingAlertStore.addAlert({
          type: 'danger',
          message: I18n.text('dealPlan.milestones.deleteError')
        });
      }
    });
  }, [deleteMilestoneMutation]);
  return {
    deleteMilestone
  };
}